import { APIEndpoints } from '@/@admin/types/constants';
import type {
    GetUserGroupsRequest,
    ManageUserGroupsRequest,
    ManageUserGroupsResponse,
    UserGroupsResponse,
} from '@/@admin/types/group-management';
import { buildCacheKey } from '@/@admin/utils';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

/**
 * Service class for group management operations
 */
export class GroupManagementService extends BaseService {
    constructor() {
        super(portalApi);
    }

    /**
     * Gets the groups that a user belongs to
     * @param request - Request containing user email
     * @returns Promise with user groups
     */
    async getUserGroups(request: GetUserGroupsRequest): Promise<UserGroupsResponse> {
        try {
            const cacheKey = buildCacheKey('adminGetUserGroups', { email: request.email });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData as UserGroupsResponse;

            const { data: response } = await this.axiosInstance.get<UserGroupsResponse>(
                APIEndpoints.GET_USER_GROUPS,
                { params: { email: request.email } },
            );

            // Cache the response data
            this.setCachedData(cacheKey, response);
            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get user groups: ${errorMessage}`),
                'studio.GroupManagementService.getUserGroups',
            );
            throw error;
        }
    }

    /**
     * Manages user groups by adding and/or removing them from specified groups
     * @param request - Request containing user email and groups to add/remove
     * @returns Promise with success message and updated groups
     */
    async manageUserGroups(request: ManageUserGroupsRequest): Promise<ManageUserGroupsResponse> {
        try {
            const { data: response } = await this.axiosInstance.post<ManageUserGroupsResponse>(
                APIEndpoints.MANAGE_USER_GROUPS,
                request,
            );

            // Invalidate cache for this user
            this.invalidateCache(buildCacheKey('adminGetUserGroups', { email: request.email }));

            return response;
        } catch (error) {
            // Enhance error for better debugging
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to manage user groups: ${errorMessage}`),
                'studio.GroupManagementService.manageUserGroups',
            );
            throw error;
        }
    }
}

export const groupManagementService = new GroupManagementService();
